import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { ApiBase } from '../apis/ApiBase';
import { authClient } from '../apis/client-instances';
import { alert } from '../hooks/function';
import { validateEmail } from '../utils/validation';
import { VisibilityIcon, VisibilityOffIcon } from './icons';
import { Box, Button, IconButton, InputAdornment, Modal, Step, StepLabel, Stepper, TextField, Typography } from './ui';

const steps = ['Enter Email', 'Verify Code', 'Set New Password'];

export default function ForgotPasswordModal({ open, onClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const reset = () => {
    setActiveStep(0);
    setEmail('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  useEffect(() => {
    // reset();
  }, [open]);

  const handleNext = async () => {
    try {
      if (activeStep === 0) {
        const emailValidation = validateEmail(email);
        if (!emailValidation.isValid) return alert(enqueueSnackbar, emailValidation.error, 'error');
        await authClient.sendVerifyCode({ email });
        setActiveStep(1);
      } else if (activeStep === 1) {
        if (!code) return alert(enqueueSnackbar, 'Please enter verification code', 'error');
        await authClient.validateVerifyCode({ email, code });
        setActiveStep(2);
      } else if (activeStep === 2) {
        if (newPassword.length <= 0) return alert(enqueueSnackbar, 'Please enter new password', 'error');
        if (newPassword !== confirmPassword) return alert(enqueueSnackbar, 'Passwords do not match', 'error');
        await authClient.forgotPassword({ email, code, newPassword });

        alert(enqueueSnackbar, 'Password reset successfully', 'success');

        onClose();
        setActiveStep(0);
        setEmail('');
        setCode('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error: any) {
      alert(enqueueSnackbar, ApiBase.getCleanErrorMessage(error), 'error');
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 450,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Typography variant="h5" fontWeight="bold" align="center">
          Forgot Password
        </Typography>

        {/* Stepper */}
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1 - Enter Email */}
        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography>Enter your email to receive a verification code.</Typography>

            <TextField
              fullWidth
              label="Email Address"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleNext();
                }
              }}
            />
            <Button variant="contained" color="primary" onClick={handleNext}>
              Send Code
            </Button>
          </Box>
        )}

        {/* Step 2 - Verify Code */}
        {activeStep === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography>
              Enter the verification code sent to <b>{email}</b>.
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              variant="outlined"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleNext();
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={handleBack} color="inherit">
                Back
              </Button>
              <Button variant="contained" color="primary" onClick={handleNext}>
                Verify Code
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3 - Set New Password */}
        {activeStep === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography>Enter and confirm your new password.</Typography>

            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleNext();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleNext();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={handleBack} color="inherit">
                Back
              </Button>
              <Button variant="contained" color="primary" onClick={handleNext}>
                Reset Password
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
